import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { useGetCharacterQuery } from "../services";
import { DYNAMIC_TASK_TYPE, GPT_MODEL_TYPES, ICharacterSettingInput, IRAGSetting, RAG_MODEL_TYPES } from "../types";
import { CharacterSettingSchema } from "../validations/character";
import useCharacterSettingSelectInput from "./useCharacterSettingSelectInput";

interface IProps {
    characterId: string | undefined;
    projectId: string | undefined;
}

const useCharacterSettingForm = ({ characterId, projectId }: IProps) => {
    const { data: characters, isLoading: cIsLoading } = useGetCharacterQuery(
        { id: characterId as string },
        { skip: !characterId }
    );
    const preUpdateCharacter = characters?.data?.character;
    const hasInitialLoadedRef = useRef(false);

    const normalizeRagSetting = (ragSetting?: IRAGSetting | null): IRAGSetting | undefined => {
        if (!ragSetting) {
            // 新角色（API 無 rag_setting）：預設為 6000
            return {
                dense_top_k: 5,
                score_threshold: 0.5,
                sparse_top_k: 5,
                top_k: 5,
                max_doc_segments: 6000,
                use_references: false,
            };
        }
        // 既有角色：正規化 max_doc_segments
        let maxDocSegments = ragSetting.max_doc_segments ?? 6000; // API null → 預設 6000
        // 向後相容：後端已將 -1 視為 6000，前端同步處理
        if (maxDocSegments === -1 ) maxDocSegments = 6000;
        return {
            ...ragSetting,
            max_doc_segments: maxDocSegments,
            use_references: ragSetting.use_references ?? false,
        };
    };

    const defaultRef = useRef<ICharacterSettingInput>({
        rag_model_id: preUpdateCharacter?.rag_model.toString() as RAG_MODEL_TYPES | null,
        gpt_model_id: preUpdateCharacter?.gpt_model.toString() as GPT_MODEL_TYPES | null,
        service_type: preUpdateCharacter?.service_type.toString() ?? "1",
        enable_context: preUpdateCharacter?.enable_context || false,
        max_context_words: preUpdateCharacter?.max_context_words ?? 500,
        enable_hint: preUpdateCharacter?.enable_hint || false,
        task_type: preUpdateCharacter?.task_type
            ? (preUpdateCharacter.task_type.toString() as DYNAMIC_TASK_TYPE)
            : ("0" as DYNAMIC_TASK_TYPE),
        hint_number: preUpdateCharacter?.hint_number ?? 2,
        temperature: preUpdateCharacter?.temperature ?? 0,
        primary_language: preUpdateCharacter?.primary_language ?? "auto",
        rag_setting: normalizeRagSetting(preUpdateCharacter?.rag_setting),
    });

    const defaultValues = defaultRef.current;
    const methods = useForm<ICharacterSettingInput>({
        mode: "onChange",
        defaultValues,
        resolver: yupResolver(CharacterSettingSchema()),
    });
    const { handleFormSubmit, getCharacterLoading } = useCharacterSettingSelectInput({
        characterId: characterId as string,
        projectId: projectId as string,
    });

    const [isResettingLanguage, setIsResettingLanguage] = useState(false);

    // 處理「回復預設」按鈕：將語言設為 'auto' 並直接發送 API 請求
    const handleResetLanguageToAuto = useCallback(async () => {
        setIsResettingLanguage(true);
        try {
            // 取得當前表單值並將 primary_language 設為 'auto'
            const currentValues = methods.getValues();
            const dataToSubmit: ICharacterSettingInput = {
                ...currentValues,
                primary_language: "auto",
            };

            // 發送 API 請求
            await handleFormSubmit(dataToSubmit);

            // 更新表單值並重置 dirty 狀態
            methods.setValue("primary_language", "auto", { shouldDirty: false });
            methods.reset({ ...currentValues, primary_language: "auto" });
        } finally {
            setIsResettingLanguage(false);
        }
    }, [methods, handleFormSubmit]);

    //重整後須帶入來自 api 的 characters 值
    useEffect(() => {
        // 允許初次載入時更新，或當表單未被修改時更新
        const shouldUpdate = characters !== undefined && (!methods.formState.isDirty || !hasInitialLoadedRef.current);

        if (shouldUpdate) {
            defaultRef.current = {
                gpt_model_id: preUpdateCharacter?.gpt_model.toString() as GPT_MODEL_TYPES,
                rag_model_id: preUpdateCharacter?.rag_model.toString() as RAG_MODEL_TYPES,
                enable_context: !!preUpdateCharacter?.enable_context,
                max_context_words: preUpdateCharacter?.max_context_words,
                service_type: preUpdateCharacter?.service_type.toString() ?? "0",
                enable_hint: preUpdateCharacter?.enable_hint || false,
                task_type: preUpdateCharacter?.task_type
                    ? (preUpdateCharacter.task_type.toString() as DYNAMIC_TASK_TYPE)
                    : ("0" as DYNAMIC_TASK_TYPE),
                hint_number: preUpdateCharacter?.hint_number ?? 2,
                temperature: preUpdateCharacter?.temperature ?? 0,
                primary_language: preUpdateCharacter?.primary_language ?? "auto",
                rag_setting: normalizeRagSetting(preUpdateCharacter?.rag_setting),
            };
            methods.reset(defaultRef.current);
            hasInitialLoadedRef.current = true; // 標記已完成初次載入
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        characters,
        methods,
        preUpdateCharacter?.enable_context,
        preUpdateCharacter?.gpt_model,
        preUpdateCharacter?.max_context_words,
        preUpdateCharacter?.rag_model,
        preUpdateCharacter?.service_type,
        preUpdateCharacter?.enable_hint,
        preUpdateCharacter?.task_type,
        preUpdateCharacter?.hint_number,
        preUpdateCharacter?.temperature,
        preUpdateCharacter?.primary_language,
        preUpdateCharacter?.rag_setting, // 重新加入 rag_setting，初次載入時需要此依賴
    ]);

    return {
        methods,
        defaultValues,
        cIsLoading,
        handleFormSubmit,
        getCharacterLoading,
        handleResetLanguageToAuto,
        isResettingLanguage,
    };
};

export default useCharacterSettingForm;
